import { MobileModal } from '@components/MobileModal/MobileModal';
import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import { formatLibraryCardMeta } from '@lib/utils/libraryStats';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { CustomWordHintsToggle } from '../CustomWordHintsToggle';
import { LibraryCard } from '../LibraryCard';
import { useLibraryEditForm } from '../useLibraryEditForm';
import css from './MobileEditLibraryModal.module.scss';
import { MobileThumbnailGrid } from './MobileThumbnailGrid';

type MobileEditLibraryModalProps = {
  open: boolean;
  onClose: () => void;
  libraryId: string;
  currentName: string;
};

export const MobileEditLibraryModal = observer(
  ({ open, onClose, libraryId, currentName }: MobileEditLibraryModalProps) => {
    const {
      library,
      videoFiles,
      newName,
      setNewName,
      newThumbnailMediaId,
      setNewThumbnailMediaId,
      glossary,
      setGlossary,
      useWordHints,
      setUseWordHints,
      isSavingChanges,
      changes,
      seed,
      reset,
      submit,
    } = useLibraryEditForm(libraryId, currentName);
    const autoFocusOnDesktop = useDesktopAutoFocus();

    // Registration and seeding are split because they answer different questions and the old
    // combined effect got both wrong: it registered on `open && library` but released on `open`
    // alone, and omitted `library` from its deps — so a library that resolved AFTER the sheet
    // opened was never registered at all, and opening before it resolved released something that
    // was never acquired.
    useModalRegistration('mobile-library-edit', open && !!library);

    // Seed exactly once per opening, on the first render where the library is actually available.
    //
    // The guard is a ref rather than just `[open, library]` deps, because `library` is not a stable
    // fact: it comes from `LibraryManager.libraries`, which filters on `data !== null`, so ANY
    // failed background refresh drops the entry and the next success restores it
    // (see .claude/rules/dataframe-absence.md). Keyed on `library` alone, that flicker would re-run
    // `seed()` mid-edit and silently reset the name, thumbnail and glossary the user had typed.
    const hasSeededRef = useRef(false);
    useEffect(() => {
      if (!open) {
        hasSeededRef.current = false;
        return;
      }
      if (!library || hasSeededRef.current) return;
      hasSeededRef.current = true;
      seed();
      // `seed` is recreated per render by useLibraryEditForm; re-running on its identity would
      // clobber whatever the user has typed.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, library]);

    if (!library) return null;

    // Same builder as the grid card and the desktop rename preview — this is a preview of that
    // card, so it must not word its meta lines independently.
    const cardMeta = formatLibraryCardMeta(library);

    const handleClose = () => {
      reset();
      onClose();
    };

    const handleSubmit = async () => {
      if (await submit()) {
        handleClose();
      }
    };

    const footer = (
      <>
        <ActionButton
          variant="secondary"
          onClick={handleClose}
          disabled={isSavingChanges}
          padding={32}
        >
          Cancel
        </ActionButton>
        <ActionButton
          onClick={handleSubmit}
          disabled={isSavingChanges || !changes.hasChanges}
          padding={32}
        >
          {isSavingChanges ? 'Save changes...' : 'Save changes'}
        </ActionButton>
      </>
    );

    return (
      <MobileModal
        open={open}
        onClose={handleClose}
        title="Edit library"
        subtitle="You can update your library name, thumbnail, and word hints below."
        footer={footer}
      >
        <div className={css.formContent}>
          {/* Card preview */}
          <div className={css.cardPreview}>
            <LibraryCard
              id={libraryId}
              name={newName}
              mediaCount={cardMeta.mediaCount ?? undefined}
              folderCount={cardMeta.folderCount}
              ownerLine={cardMeta.owner ?? undefined}
              library={library}
              hideOptions
              thumbnailMediaId={newThumbnailMediaId}
              noLink
            />
          </div>

          {/* Library name */}
          <div className={css.field}>
            <label className={css.label}>Library name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onFocus={(e) => e.target.select()}
              className={css.input}
              disabled={isSavingChanges}
              placeholder={currentName}
              autoFocus={autoFocusOnDesktop}
            />
          </div>

          <CustomWordHintsToggle
            checked={useWordHints}
            onChange={setUseWordHints}
            words={glossary}
            onWordsChange={setGlossary}
            showAppliesToNewMediaWarning={(library.stats?.count ?? 0) > 0}
            disabled={isSavingChanges}
          />

          {/* Thumbnail selection */}
          {videoFiles.length > 0 && (
            <div className={css.field}>
              <label className={css.label}>Select thumbnail</label>
              <MobileThumbnailGrid
                libraryId={libraryId}
                folderFiles={videoFiles}
                selectedMediaId={newThumbnailMediaId}
                onSelectMedia={setNewThumbnailMediaId}
              />
            </div>
          )}
        </div>
      </MobileModal>
    );
  },
);
